/**
 * Разметка тактики: геометрия доски и выгрузка в картинку.
 *
 * Здесь всё, что можно посчитать без разметки страницы, — то же правило, по
 * которому живёт `lib/stats.ts`. Доска остаётся про вид: она берёт точку у
 * мыши и отдаёт её сюда, а куда попала фигура и что стирать — решается
 * функциями, закрытыми тестами.
 */

import { TACTIC_ARROW_LABEL, type TacticCar, type TacticPoint, type TacticShape } from '@ak/shared';

/** Точка в долях карты: 0 — левый верхний угол, 1 — правый нижний. */
export function toUnitPoint(
  clientX: number,
  clientY: number,
  box: { left: number; top: number; width: number; height: number },
): TacticPoint {
  if (box.width <= 0 || box.height <= 0) {
    return [0, 0];
  }

  const x = (clientX - box.left) / box.width;
  const y = (clientY - box.top) / box.height;

  return [clamp(x), clamp(y)];
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, Math.round(value * 10000) / 10000));
}

/**
 * Расстояние от точки до фигуры — в долях карты.
 *
 * Нужно ластику: он стирает фигуру целиком, и «попал» определяется здесь, а
 * не браузером. Кисть — ломаная, поэтому меряется до ближайшего звена, а не
 * до её первой точки: иначе стереть длинную линию можно было бы только
 * ткнув в её начало.
 */
export function distanceToShape(shape: TacticShape, point: TacticPoint): number {
  if (shape.kind === 'stroke') {
    let best = Number.POSITIVE_INFINITY;
    for (let index = 0; index + 3 < shape.points.length; index += 2) {
      const from: TacticPoint = [shape.points[index] ?? 0, shape.points[index + 1] ?? 0];
      const to: TacticPoint = [shape.points[index + 2] ?? 0, shape.points[index + 3] ?? 0];
      best = Math.min(best, distanceToSegment(point, from, to));
    }

    return best;
  }

  if (shape.kind === 'arrow') {
    return distanceToSegment(point, shape.from, shape.to);
  }

  if (shape.kind === 'car') {
    /*
     * Машина — не точка: она занимает почти десятую долю карты. Мерить до её
     * середины значило бы, что ластик не стирает капот, а рукой машину не
     * взять за край — тычок туда, где она нарисована, промахивался мимо.
     */
    return Math.max(0, distance(point, shape.at) - CAR_BOX.length * 0.35);
  }

  return distance(point, shape.at);
}

function distance(left: TacticPoint, right: TacticPoint): number {
  return Math.hypot(left[0] - right[0], left[1] - right[1]);
}

function distanceToSegment(point: TacticPoint, from: TacticPoint, to: TacticPoint): number {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return distance(point, from);
  }

  const t = Math.min(1, Math.max(0, ((point[0] - from[0]) * dx + (point[1] - from[1]) * dy) / lengthSquared));
  return distance(point, [from[0] + t * dx, from[1] + t * dy]);
}

/**
 * Что стирает ластик: верхнюю фигуру под курсором.
 *
 * Верхнюю, потому что она же нарисована поверх остальных — стирается то, во
 * что человек ткнул глазами. `null` — рядом ничего нет, и клик по пустому
 * месту ничего не портит.
 */
export function pickShapeAt(shapes: readonly TacticShape[], point: TacticPoint, radius = 0.02): number | null {
  /*
   * Побеждает ближайшая, а не первая сверху.
   *
   * Машина занимает почти десятую долю карты, и допуск вокруг неё широкий:
   * рядом стоящая соседка попадала в него тоже — и, если она нарисована
   * позже, рука брала её, хотя тыкали в первую. При равном расстоянии берётся
   * та, что нарисована выше: в неё и целились.
   */
  let best: { index: number; distance: number } | null = null;

  for (let index = 0; index < shapes.length; index += 1) {
    const shape = shapes[index];
    if (shape === undefined) {
      continue;
    }

    const away = distanceToShape(shape, point);
    if (away <= radius && (best === null || away <= best.distance)) {
      best = { index, distance: away };
    }
  }

  return best?.index ?? null;
}

/**
 * Куда смотрит машина.
 *
 * Угол считается от положения на карте: нос всегда развёрнут к середине —
 * туда, где точка капта. Хранить угол в фигуре не нужно, и при перетаскивании
 * машина доворачивается сама.
 *
 * Ноль — это «носом вверх», как на картинке; дальше по часовой стрелке.
 */
export function carAngle(at: TacticPoint): number {
  const dx = 0.5 - at[0];
  const dy = 0.5 - at[1];

  // Машина в самой середине никуда не смотрит — оставляем носом вверх.
  if (Math.hypot(dx, dy) < 0.02) {
    return 0;
  }

  const degrees = (Math.atan2(dy, dx) * 180) / Math.PI + 90;

  // Приводим к промежутку (-180, 180]: поворот на 270° и на -90° — одно и то
  // же, но читать в отладке проще второе.
  return degrees > 180 ? degrees - 360 : degrees <= -180 ? degrees + 360 : degrees;
}

/** Все точки фигуры — по ним считаются её габариты. */
function shapePoints(shape: TacticShape): TacticPoint[] {
  if (shape.kind === 'stroke') {
    const points: TacticPoint[] = [];
    for (let index = 0; index + 1 < shape.points.length; index += 2) {
      points.push([shape.points[index] ?? 0, shape.points[index + 1] ?? 0]);
    }

    return points;
  }

  return shape.kind === 'arrow' ? [shape.from, shape.to] : [shape.at];
}

/**
 * Двигает фигуру целиком.
 *
 * Нужна руке: она таскает по карте и линию, и стрелку, и машину, а точки у
 * них лежат по-разному.
 *
 * По краям обрезается сам сдвиг, а не каждая точка по отдельности. Обрезание
 * точек ломало фигуру: дошедшие до края вставали, а хвост продолжал ехать —
 * длинная линия у левого края сминалась и обратно уже не расправлялась.
 */
export function moveShape(shape: TacticShape, dx: number, dy: number): TacticShape {
  const points = shapePoints(shape);
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);

  const limitedX = Math.min(1 - Math.max(...xs), Math.max(-Math.min(...xs), dx));
  const limitedY = Math.min(1 - Math.max(...ys), Math.max(-Math.min(...ys), dy));

  const shift = (point: TacticPoint): TacticPoint => [clamp(point[0] + limitedX), clamp(point[1] + limitedY)];

  switch (shape.kind) {
    case 'stroke':
      return {
        ...shape,
        points: shape.points.map((value, index) => clamp(value + (index % 2 === 0 ? limitedX : limitedY))),
      };
    case 'arrow':
      return { ...shape, from: shift(shape.from), to: shift(shape.to) };
    default:
      return { ...shape, at: shift(shape.at) };
  }
}

/**
 * Следующий номер метки при кручении колеса.
 *
 * Номера ходят по кругу от 1 до 99: докрутив до конца, человек возвращается
 * к началу, а не упирается.
 */
export function rollMarkerLabel(label: string, direction: number): string {
  const current = Number.parseInt(label, 10);
  if (!Number.isInteger(current)) {
    return label;
  }

  const next = current + (direction > 0 ? 1 : -1);
  return String(next < 1 ? 99 : next > 99 ? 1 : next);
}

/**
 * Отбор людей по нику для панели состава и окна экипажа.
 *
 * В составе капта до тридцати пяти человек, и найти нужного глазами дольше,
 * чем набрать три буквы.
 *
 * Уже отмеченные остаются в списке при любом запросе: иначе снять отметку
 * можно было бы, только стерев запрос, — человек набрал ник, посадил, набрал
 * следующий, а первого уже не видно и не убрать.
 */
export function filterByNick<T extends { gameNick: string }>(
  people: readonly T[],
  query: string,
  keep: readonly string[] = [],
): T[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return [...people];
  }

  return people.filter(
    (person) => keep.includes(person.gameNick) || person.gameNick.toLocaleLowerCase().includes(needle),
  );
}

/**
 * Коллеры — наверх списка.
 *
 * Коллер ведёт состав голосом, и машину набирают вокруг него: сначала решают,
 * кто зовёт, потом кто с ним едет. Внутри каждой половины порядок остаётся
 * прежним — тот, в котором люди записались в набор.
 */
export function callersFirst<T extends { caller?: boolean }>(people: readonly T[]): T[] {
  return [...people.filter((person) => person.caller === true), ...people.filter((person) => person.caller !== true)];
}

/** Короткая подпись фигуры для списка слоёв. */
export function describeShape(shape: TacticShape): string {
  switch (shape.kind) {
    case 'stroke':
      return shape.dashed ? 'Пунктир' : 'Маркер';
    case 'arrow':
      return TACTIC_ARROW_LABEL[shape.variant];
    case 'marker':
      return `Метка ${shape.label}`;
    case 'car':
      return shape.crew.length === 0 ? 'Машина (пустая)' : `Машина: ${shape.crew.join(', ')}`;
  }
}

/**
 * Размеры машины на доске — в долях ширины карты.
 *
 * Одни и те же числа нужны и слою разметки, и выгрузке на холст: если они
 * разъедутся, скачанная картинка перестанет совпадать с тем, что человек
 * видел на экране.
 */
/**
 * Наименьший наконечник стрелки и прицел — в долях ширины карты.
 *
 * Общие для доски и для выгрузки: пока порог у одной был в пикселях, а у
 * другой в долях, тонкая стрелка в скачанной картинке выходила вдвое меньше,
 * чем на экране.
 */
export const ARROW_HEAD = 0.02;
export const FIRE_SIGHT = 0.006;

export const CAR_BOX = {
  /**
   * Длина машины — доля ширины карты. Машина стоит носом вверх.
   *
   * Подобрано по доске: при 0,062 машина выходила размером с метку, и вместо
   * машины на карте было красное пятнышко.
   */
  length: 0.088,
  /**
   * Пропорции коробки под картинку. Картинки машин квадратные (512 × 512), и
   * машина занимает середину: по бокам прозрачно. Коробка тоже квадратная —
   * иначе выгрузка на холст растянула бы машину поперёк.
   */
  ratio: 1,
  /** Высота строки с ником и кегль — тоже в долях. Мелко и плотно: ники
   * подписывают машину, а не спорят с названиями кварталов на карте. */
  line: 0.0155,
  text: 0.0125,
  /** Отступ списка от кузова, чтобы ники не наезжали на машину. */
  gap: 0.012,
} as const;

/**
 * Плашка с экипажем: где её поставить, чтобы она не легла на соседнюю.
 *
 * Ники висели списком справа от машины. Две полные машины рядом — и списки
 * наезжали друг на друга: читать нельзя, чей экипаж где, тоже непонятно.
 * Теперь у каждой машины плашка, и плашки расставляются по свободной стороне.
 *
 * Считается один раз на всю доску и одинаково для экрана и для выгрузки:
 * разъехавшись, они показали бы разное на экране и в скачанной картинке.
 *
 * Все размеры — в долях ширины карты, включая вертикальные: так же считает
 * машина. Наружу вертикаль отдаётся в долях высоты (`ratio` переводит), чтобы
 * обе стороны рисовальщика умножали доли на свои размеры без поправок.
 */
export const CAR_LABEL = {
  /** Поля внутри плашки. */
  padX: 0.007,
  padY: 0.004,
  /**
   * Ширина символа в долях кегля.
   *
   * Оценка, а не замер: замерить текст умеет холст, но не SVG, а раскладка
   * обязана совпасть с точностью до пикселя. Средняя кириллическая буква в
   * Inter полужирного начертания занимает чуть больше половины кегля.
   */
  charWidth: 0.55,
} as const;

/** Куда встала плашка: доли карты, левый верхний угол. */
export interface CarLabelBox {
  /** Номер фигуры в разметке — по нему рисовальщик находит свою машину. */
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  lines: string[];
  color: string;
  /** Середина машины: от неё к плашке идёт поводок. */
  from: readonly [number, number];
}

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

function overlaps(left: Box, right: Box): boolean {
  return (
    left.x < right.x + right.width &&
    right.x < left.x + left.width &&
    left.y < right.y + right.height &&
    right.y < left.y + left.height
  );
}

function plateSize(crew: readonly string[]): { width: number; height: number } {
  const longest = crew.reduce((max, name) => Math.max(max, name.length), 0);

  return {
    width: longest * CAR_BOX.text * CAR_LABEL.charWidth + CAR_LABEL.padX * 2,
    height: crew.length * CAR_BOX.line + CAR_LABEL.padY * 2,
  };
}

/**
 * Плашка под курсором. `null` — там пусто.
 *
 * Плашки лежат поверх фигур, поэтому рука спрашивает сначала про них: иначе
 * нажатие на ники брало бы то, что нарисовано под ними.
 */
export function pickCarLabelAt(labels: readonly CarLabelBox[], point: TacticPoint): CarLabelBox | null {
  for (let index = labels.length - 1; index >= 0; index -= 1) {
    const label = labels[index];
    if (
      label !== undefined &&
      point[0] >= label.x &&
      point[0] <= label.x + label.width &&
      point[1] >= label.y &&
      point[1] <= label.y + label.height
    ) {
      return label;
    }
  }

  return null;
}

/**
 * Расставляет плашки экипажей.
 *
 * Для каждой машины перебираются четыре места — справа, слева, снизу, сверху,
 * — и берётся первое, которое не залезает ни на другую машину, ни на уже
 * поставленную плашку, ни за край карты. Если заняты все четыре, плашка
 * сдвигается вниз, пока не найдётся место: пусть стоит криво, но читается.
 *
 * Порядок разбора — порядок фигур, поэтому раскладка не пляшет от вызова к
 * вызову: первая поставленная машина держит своё место, доезжают соседи.
 */
export function layoutCarLabels(shapes: readonly TacticShape[], ratio: number): CarLabelBox[] {
  const half = CAR_BOX.length / 2;
  const gap = CAR_BOX.gap;

  const cars = shapes
    .map((shape, index) => ({ shape, index }))
    .filter(
      (entry): entry is { shape: TacticCar; index: number } =>
        entry.shape.kind === 'car' && entry.shape.crew.length > 0,
    );

  /*
   * Всё считается в долях ширины карты — и по вертикали тоже: машина
   * квадратная, и её половина одинакова по обеим осям. Точки фигур приходят в
   * долях высоты, поэтому на входе они делятся на пропорции, а на выходе
   * умножаются обратно. На квадратных картах это ничего не меняет, но карта
   * может быть и не квадратной.
   */
  const bottom = 1 / ratio;

  // Коробки самих машин: плашка не должна ложиться ни на одну из них.
  const taken: Box[] = shapes
    .filter((shape): shape is TacticCar => shape.kind === 'car')
    .map((car) => ({ x: car.at[0] - half, y: car.at[1] / ratio - half, width: half * 2, height: half * 2 }));

  const boxes: CarLabelBox[] = [];

  for (const { shape, index } of cars) {
    const { width, height } = plateSize(shape.crew);
    const cx = shape.at[0];
    const cy = shape.at[1] / ratio;

    const candidates: Box[] = [
      { x: cx + half + gap, y: cy - height / 2, width, height },
      { x: cx - half - gap - width, y: cy - height / 2, width, height },
      { x: cx - width / 2, y: cy + half + gap, width, height },
      { x: cx - width / 2, y: cy - half - gap - height, width, height },
    ];

    const fits = (box: Box): boolean =>
      box.x >= 0 &&
      box.y >= 0 &&
      box.x + box.width <= 1 &&
      box.y + box.height <= bottom &&
      !taken.some((other) => overlaps(box, other));

    // Плашку двигали руками — стоит там, куда поставили, и место ей не ищут.
    let chosen =
      shape.labelAt === null
        ? candidates.find(fits)
        : { x: shape.labelAt[0], y: shape.labelAt[1] / ratio, width, height };

    if (chosen === undefined) {
      // Все четыре стороны заняты — сползаем вниз от правой, пока не влезем.
      const first = candidates[0] ?? { x: cx, y: cy, width, height };
      for (let step = 0; step < 12; step += 1) {
        const shifted = { ...first, y: first.y + step * (height + gap) };
        if (fits(shifted)) {
          chosen = shifted;
          break;
        }
      }

      chosen ??= first;
    }

    /*
     * Последним делом плашка загоняется в карту.
     *
     * У края все четыре места не влезают, и раньше плашка оставалась за
     * краем — ники просто пропадали. Пусть лучше сдвинется и наложится на
     * что-нибудь: ники на карте важнее, чем чистая раскладка.
     */
    chosen = {
      ...chosen,
      x: Math.min(1 - width, Math.max(0, chosen.x)),
      y: Math.min(bottom - height, Math.max(0, chosen.y)),
    };

    taken.push(chosen);
    boxes.push({
      index,
      x: chosen.x,
      // Наружу вертикаль уходит в долях высоты карты: рисовальщики умножают
      // её на свою высоту, а считалось всё в долях ширины.
      y: chosen.y * ratio,
      width: chosen.width,
      height: chosen.height * ratio,
      lines: [...shape.crew],
      color: shape.color,
      from: [cx, shape.at[1]],
    });
  }

  return boxes;
}

/**
 * Куда встаёт плашка экипажа, пока её тащат.
 *
 * `grabbedAt` — где плашка стояла **в момент захвата**, и это здесь главное.
 * Смещение `dx`/`dy` всегда считается от начала перетаскивания, поэтому и
 * основа обязана быть оттуда же.
 *
 * Раньше основу брали из свежей раскладки на каждом движении мыши. Пока
 * плашку не двигали руками, `labelAt` у машины пуст, и основой становилось
 * её текущее — уже сдвинутое — место: к нему прибавлялось смещение от начала
 * перетаскивания, то есть само себя. Плашка убегала от курсора с ускорением
 * и оказывалась где угодно. Ломалось это только у машины, которую сейчас
 * поставили: у той, чью плашку уже двигали, основа бралась из `labelAt` и
 * была неподвижна.
 */
export function moveCarLabel(car: TacticCar, grabbedAt: TacticPoint | null, dx: number, dy: number): TacticCar {
  const from = car.labelAt ?? grabbedAt ?? car.at;

  return { ...car, labelAt: [from[0] + dx, from[1] + dy] };
}

/**
 * Ложится ли отпущенный жест в разметку одним шагом истории.
 *
 * Отдельной функцией, потому что цена ошибки тут незаметная: перенос не
 * падает и не ругается, он просто пропадает. Плашку экипажа записывать
 * забыли — её режим в проверке не значился, — и она возвращалась на место,
 * едва её отпускали, хотя за курсором шла правильно.
 *
 * `moved` — двигали ли вообще: нажатие без движения переносом не считается,
 * иначе каждый щелчок по машине оставлял бы шаг в истории отмены.
 */
export function shouldCommitDrag(mode: 'shape' | 'label' | 'pan', moved: boolean): boolean {
  return moved && mode !== 'pan';
}

/** Ширина машины считается по картинке, чтобы её не сплющивало. */
export function carSize(scale: number): { length: number; width: number } {
  const length = CAR_BOX.length * scale;
  return { length, width: length * CAR_BOX.ratio };
}

/**
 * Следующий номер метки.
 *
 * Метки нумеруются сами: расставляя точки состава, никто не хочет вводить
 * «1», «2», «3» руками. Номер берётся не по количеству фигур, а по самому
 * большому уже стоящему — иначе после стирания второй метки третья стала бы
 * второй, а на карте оказались бы две одинаковых.
 */
export function nextMarkerLabel(shapes: readonly TacticShape[]): string {
  const highest = shapes.reduce((max, shape) => {
    if (shape.kind !== 'marker') {
      return max;
    }

    const value = Number.parseInt(shape.label, 10);
    return Number.isInteger(value) ? Math.max(max, value) : max;
  }, 0);

  return String(Math.min(highest + 1, 99));
}

/** Рисует фигуры на холсте поверх карты — этим же кодом делается выгрузка. */
export function drawShapes(
  context: CanvasRenderingContext2D,
  shapes: readonly TacticShape[],
  size: { width: number; height: number },
  /**
   * Картинки машин по номерам — те же, что на доске. Пусто или не
   * подгрузилось — машина рисуется вектором: выгрузка не должна срываться
   * из-за значка.
   */
  carImages?: readonly (CanvasImageSource | null)[],
  /** Плашки с никами не рисовать — то же, что кнопка «скрыть экипажи». */
  hideCrew = false,
): void {
  const scale = size.width;

  for (const shape of shapes) {
    context.strokeStyle = shape.color;
    context.fillStyle = shape.color;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.setLineDash([]);

    if (shape.kind === 'stroke') {
      context.lineWidth = shape.width * scale;
      if (shape.dashed) {
        const step = Math.max(shape.width * scale * 2.2, 4);
        context.setLineDash([step, step * 0.9]);
      }

      context.beginPath();
      for (let index = 0; index + 1 < shape.points.length; index += 2) {
        const x = (shape.points[index] ?? 0) * size.width;
        const y = (shape.points[index + 1] ?? 0) * size.height;
        if (index === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      }

      context.stroke();
      continue;
    }

    if (shape.kind === 'arrow') {
      const from: TacticPoint = [shape.from[0] * size.width, shape.from[1] * size.height];
      const to: TacticPoint = [shape.to[0] * size.width, shape.to[1] * size.height];
      const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
      const line = shape.width * scale;

      if (shape.variant === 'move') {
        // Заход — шаги: пунктир, у которого штрих и пропуск равны толщине
        // линии, читается как след, а не как «недорисованная» стрелка.
        const step = Math.max(line * 2.4, 6);
        context.lineWidth = line;
        context.setLineDash([step, step * 0.8]);
      } else {
        // Огонь — сплошная тонкая линия: она не «идёт», она указывает.
        context.lineWidth = Math.max(line * 0.65, 1);
      }

      context.beginPath();
      context.moveTo(from[0], from[1]);
      context.lineTo(to[0], to[1]);
      context.stroke();
      context.setLineDash([]);

      if (shape.variant === 'move') {
        // Порог в долях ширины, как и на доске: в пикселях наконечник тонкой
        // стрелки в скачанной картинке выходил вдвое меньше, чем на экране.
        const head = Math.max(line * 4, scale * ARROW_HEAD);
        context.beginPath();
        context.moveTo(to[0], to[1]);
        context.lineTo(to[0] - head * Math.cos(angle - Math.PI / 7), to[1] - head * Math.sin(angle - Math.PI / 7));
        context.lineTo(to[0] - head * Math.cos(angle + Math.PI / 7), to[1] - head * Math.sin(angle + Math.PI / 7));
        context.closePath();
        context.fill();
        continue;
      }

      // Прицел на конце: «стреляем сюда» видно без легенды.
      const radius = Math.max(line * 2.2, scale * FIRE_SIGHT);
      context.lineWidth = Math.max(line * 0.65, 1);
      context.beginPath();
      context.arc(to[0], to[1], radius, 0, Math.PI * 2);
      context.stroke();
      context.beginPath();
      context.moveTo(to[0] - radius * 1.7, to[1]);
      context.lineTo(to[0] + radius * 1.7, to[1]);
      context.moveTo(to[0], to[1] - radius * 1.7);
      context.lineTo(to[0], to[1] + radius * 1.7);
      context.stroke();
      continue;
    }

    if (shape.kind === 'car') {
      drawCar(context, shape, size, pickCarImage(carImages, shape.image));
      continue;
    }

    const x = shape.at[0] * size.width;
    const y = shape.at[1] * size.height;
    const radius = size.width * 0.018;

    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = readableInk(shape.color);
    // Начертание и положение — те же, что в разметке доски: скачанная
    // картинка обязана совпадать с тем, что человек видел на экране.
    context.font = `700 ${String(Math.round(radius * 1.3))}px Inter Variable, Segoe UI, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(shape.label, x, y);
  }

  // Плашки с экипажами — последними и все разом: раскладка считается на всю
  // доску, а положенная под чужую стрелку плашка не читалась бы.
  if (!hideCrew) {
    drawCarLabels(context, shapes, size);
  }
}

function drawCar(
  context: CanvasRenderingContext2D,
  car: { color: string; at: readonly [number, number]; crew: string[] },
  size: { width: number; height: number },
  carImage: CanvasImageSource | undefined,
): void {
  if (carImage === undefined) {
    return;
  }

  const x = car.at[0] * size.width;
  const y = car.at[1] * size.height;
  const { length, width } = carSize(size.width);

  context.save();
  context.translate(x, y);
  context.rotate((carAngle(car.at) * Math.PI) / 180);
  context.drawImage(carImage, -width / 2, -length / 2, width, length);
  context.restore();
}

/**
 * Плашки с экипажами поверх всего остального.
 *
 * Рисуются после фигур и все разом: раскладка считается на всю доску, а
 * положенная под чужую стрелку плашка не читалась бы.
 */
function drawCarLabels(
  context: CanvasRenderingContext2D,
  shapes: readonly TacticShape[],
  size: { width: number; height: number },
): void {
  const scale = size.width;
  const labels = layoutCarLabels(shapes, size.width / size.height);

  for (const label of labels) {
    const x = label.x * size.width;
    const y = label.y * size.height;
    const width = label.width * size.width;
    const height = label.height * size.height;
    const radius = CAR_LABEL.padY * scale * 1.5;

    // Поводок от машины к плашке: по нему видно, чей это экипаж, даже когда
    // плашка отъехала в сторону от тесноты.
    context.save();
    context.strokeStyle = label.color === '#000000' ? '#ffffff' : label.color;
    context.lineWidth = Math.max(scale * 0.0015, 1);
    context.beginPath();
    context.moveTo(label.from[0] * size.width, label.from[1] * size.height);
    context.lineTo(x + width / 2, y + height / 2);
    context.stroke();

    // Плашка: тёмная подложка и полоска цвета машины слева.
    context.fillStyle = 'rgba(12, 16, 24, 0.82)';
    context.beginPath();
    context.roundRect(x, y, width, height, radius);
    context.fill();

    context.fillStyle = label.color === '#000000' ? '#ffffff' : label.color;
    context.beginPath();
    context.roundRect(x, y, Math.max(scale * 0.003, 2), height, radius);
    context.fill();

    const line = CAR_BOX.line * scale;
    const fontSize = CAR_BOX.text * scale;
    context.font = `600 ${String(Math.round(fontSize))}px Inter Variable, Segoe UI, sans-serif`;
    context.textAlign = 'left';
    context.textBaseline = 'middle';
    context.fillStyle = '#ffffff';

    label.lines.forEach((name, index) => {
      context.fillText(name, x + CAR_LABEL.padX * scale, y + CAR_LABEL.padY * scale + line * (index + 0.5));
    });

    context.restore();
  }
}

/** Картинка машины по её номеру — по кругу, как и на доске. */
function pickCarImage(
  images: readonly (CanvasImageSource | null)[] | undefined,
  image: number,
): CanvasImageSource | undefined {
  if (images === undefined || images.length === 0) {
    return undefined;
  }

  const index = ((Math.trunc(image) % images.length) + images.length) % images.length;
  return images[index] ?? undefined;
}

/** Цвет надписи внутри метки: на светлом кружке — тёмный, на тёмном — светлый. */
export function readableInk(background: string): string {
  const hex = background.replace('#', '');
  if (hex.length !== 6) {
    return '#ffffff';
  }

  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return luminance > 0.6 ? '#111111' : '#ffffff';
}

/** Имя файла выгрузки: карта, сторона и дата — чтобы не искать среди «снимок(3)». */
export function exportFileName(mapName: string, presetName: string | null): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const parts = [mapName, presetName ?? '', stamp].filter((part) => part !== '');

  return `${parts.join(' — ')}.png`.replace(/[\\/:*?"<>|]/g, '-');
}
